import { useState } from 'react';
import { Button, Dialog, Field, Icon, Input, Panel } from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';

/**
 * RECOVERY CODES — the count, and the one way back to a usable set.
 *
 * ── WHY A COUNT IS THE WHOLE POINT ────────────────────────────────────────
 *
 * Codes are shown exactly once. That is correct and it has a consequence nobody
 * had addressed: somebody who has spent four of ten has NO WAY TO KNOW how many
 * remain, so they cannot tell the difference between "I have six left" and "I am
 * one lost phone away from a Super Admin". The server returns a count and never
 * the codes, which is why those are two endpoints rather than one with a flag.
 *
 * Zero is a real answer and is said loudly rather than hidden, because it is the
 * state in which losing the authenticator means losing the account.
 *
 * ── AND WHY THE NEW SET IS A DIALOG THAT WILL NOT CLOSE BY ACCIDENT ───────
 *
 * The codes come back once, in one response, and are never retrievable. A panel
 * that re-rendered them away on the next state change, or a dialog Escape could
 * dismiss, would lose them silently — and the person would not find out until the
 * day they needed one. So the set is shown in a non-dismissible dialog with an
 * explicit acknowledgement, and the acknowledgement says what it is acknowledging.
 */
export interface RecoveryCodesPanelProps {
  remaining: number;
  /** False when there is no confirmed authenticator, so there is nothing to prove. */
  canMint: boolean;
  onMinted: () => void;
  onFailure: (title: string, error: unknown) => void;
}

export function RecoveryCodesPanel({
  remaining,
  canMint,
  onMinted,
  onFailure,
}: RecoveryCodesPanelProps) {
  const [asking, setAsking] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [fresh, setFresh] = useState<{ codes: string[]; retired: number } | null>(null);

  const mint = async () => {
    if (busy || code.trim().length < 6) return;
    setBusy(true);
    setCodeError(null);
    try {
      const answer = await api.post<{ codes: string[]; retired: number }>(
        '/api/auth/account/recovery-codes',
        { code: code.trim() },
      );
      setAsking(false);
      setCode('');
      setFresh(answer);
      onMinted();
    } catch (error) {
      if (error instanceof ApiRequestError && error.fields?.['code'] !== undefined) {
        setCodeError(error.fields['code']);
      } else {
        onFailure('Could not make new recovery codes', error);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Recovery codes">
      <div className="stack">
        {remaining === 0 ? (
          <div className="notice notice-danger">
            <Icon name="warning" />
            <div>
              <strong>You have no recovery codes.</strong> If you lose your authenticator you
              will not be able to sign in, and only a Super Admin can put that right. Make a set
              now.
            </div>
          </div>
        ) : (
          <p className="text-subtle">
            {remaining === 1
              ? 'One unused code left.'
              : `${remaining} unused codes left.`}{' '}
            Each one works once, in place of a code from your authenticator.
          </p>
        )}

        {!canMint && (
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              Making codes needs a code from your authenticator, and this account has none set
              up. A Super Admin has to set one up for you first.
            </div>
          </div>
        )}

        <div>
          <Button onClick={() => setAsking(true)} disabled={!canMint}>
            Make a new set
          </Button>
        </div>

        <p className="text-subtle">
          A new set <strong>retires every code you have now</strong>. Anything written down or
          printed stops working.
        </p>
      </div>

      <Dialog
        open={asking}
        onClose={() => {
          if (!busy) setAsking(false);
        }}
        title="Make a new set of recovery codes"
        actions={
          <>
            <Button onClick={() => setAsking(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              busy={busy}
              disabled={code.trim().length < 6}
              onClick={() => void mint()}
            >
              Make new codes
            </Button>
          </>
        }
      >
        <div className="stack">
          <div className="notice notice-warn">
            <Icon name="warning" />
            <div>
              Every code you have now stops working the moment this succeeds. If you have a
              printout, it becomes waste paper.
            </div>
          </div>
          <Field
            label="Code from your authenticator"
            hint="Or one of your existing recovery codes."
            {...(codeError !== null ? { error: codeError } : {})}
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                /* `one-time-code` so a password manager does not offer to save
                   this as a credential. */
                autoComplete="one-time-code"
                inputMode="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            )}
          </Field>
        </div>
      </Dialog>

      {/* NOT DISMISSIBLE. These codes exist in this response and nowhere else;
          an Escape key here loses them for good. */}
      <Dialog
        open={fresh !== null}
        onClose={() => undefined}
        dismissible={false}
        title="Your new recovery codes"
        actions={
          <Button variant="primary" onClick={() => setFresh(null)}>
            I have saved them
          </Button>
        }
      >
        <div className="stack">
          <div className="notice notice-warn">
            <Icon name="warning" />
            <div>
              <strong>This is the only time these are shown.</strong> Save them somewhere only
              you can reach — not in the same place as your password. Each one works once.
            </div>
          </div>
          <ul className="recovery-code-list">
            {fresh?.codes.map((c) => (
              <li key={c}>
                <code>{c}</code>
              </li>
            ))}
          </ul>
          {fresh !== null && fresh.retired > 0 && (
            <p className="text-subtle">
              {fresh.retired === 1
                ? 'One older code was retired and no longer works.'
                : `${fresh.retired} older codes were retired and no longer work.`}
            </p>
          )}
        </div>
      </Dialog>
    </Panel>
  );
}
